import { ErrorMessage, Form, Formik, useField } from 'formik';

import FormField from '@/components/forms/FormField';
import { IField } from '@/components/forms/interfaces/IField';
import { PlanItem } from '@/interfaces/api/configApp/IPlanItem';
import { planValidationSchema } from '@/pages/Admin/configApp/schemas/config-plan.schema';

interface EditPlanModalProps {
	plan: PlanItem;
	onClose: () => void;
	onSave: (updatedPlan: PlanItem) => void;
}

const EditPlanModal = ({ plan, onClose, onSave }: EditPlanModalProps) => {
	const initialValues: PlanItem = {
		...plan,
		features: plan.features ? [...plan.features] : [],
	};

	const handleSubmit = (values: PlanItem) => {
		onSave(values);
	};

	const FeaturesField = ({ name }: { name: string }) => {
		const [field, , helpers] = useField<string[]>(name);
		const { value } = field;
		const { setValue } = helpers;

		const handleFeatureChange = (idx: number, val: string) => {
			const updated = [...value];
			updated[idx] = val;
			setValue(updated);
		};

		const handleAddFeature = () => {
			setValue([...value, '']);
		};

		const handleRemoveFeature = (idx: number) => {
			const updated = value.filter((_, i) => i !== idx);
			setValue(updated);
		};

		return (
			<div className="flex flex-col">
				<div className="flex justify-between items-center mb-2">
					<label className="text-sm font-medium text-gray-700">Features</label>
					<button
						type="button"
						onClick={handleAddFeature}
						className="text-sm px-2 py-1 bg-greenBrand-100 text-greenBrand-700 rounded hover:bg-greenBrand-200"
					>
						+ Add Feature
					</button>
				</div>

				<div className="space-y-2">
					{value.map((feature, idx) => (
						<div key={idx} className="flex items-center gap-2">
							<input
								type="text"
								value={feature}
								onChange={(e) => handleFeatureChange(idx, e.target.value)}
								className="border rounded-md px-3 py-2 flex-1"
							/>
							<button
								type="button"
								onClick={() => handleRemoveFeature(idx)}
								className="text-red-500 hover:text-red-700 p-2"
							>
								✕
							</button>
						</div>
					))}
				</div>
			</div>
		);
	};

	const formFields: IField[] = [
		{
			name: 'name',
			label: 'Name',
			type: 'text',
			placeholder: 'Enter plan name',
		},
		{
			name: 'price',
			label: 'Price',
			type: 'number',
			placeholder: 'Enter price',
			disabled: true,
		},
		{
			name: 'priceNote',
			label: 'Price Note',
			type: 'text',
			placeholder: 'Enter price note',
		},
		{
			name: 'limit',
			label: 'Limit',
			type: 'number',
			placeholder: 'Enter limit',
		},
		{
			name: 'pricePerExtraHaggle',
			label: 'Price Per Extra Haggle',
			type: 'number',
			placeholder: 'Enter price per extra haggle',
			disabled: true,
		},
	];

	return (
		<div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-40 p-4 ">
			<div className="bg-white rounded-lg shadow-lg w-full max-w-lg max-h-[60vh] flex flex-col overflow-hidden hover:overflow-y-scroll scroll-smooth">
				<div className="border-b p-6">
					<h2 className="text-xl font-bold text-gray-800">
						Edit Plan - {plan.name}
					</h2>
				</div>

				<Formik
					validationSchema={planValidationSchema}
					initialValues={initialValues}
					onSubmit={handleSubmit}
				>
					{() => (
						<Form className="flex-1 flex flex-col">
							{/* Scrollable Body */}
							<div className="overflow-y-auto p-6">
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									{formFields.map((field) => (
										<FormField key={field.name} {...field} />
									))}
								</div>

								<div className="mt-4 w-full pb-4">
									<FeaturesField name="features" />
									<ErrorMessage
										name="features"
										component="p"
										className="text-red-500 text-xs mt-1"
									/>
								</div>
							</div>

							<div className="border-t p-4 bg-gray-50 sticky bottom-0">
								<div className="flex justify-end gap-3">
									<button
										type="button"
										onClick={onClose}
										className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
									>
										Cancel
									</button>
									<button
										type="submit"
										className="px-4 py-2 bg-greenBrand-500 text-white rounded-md hover:opacity-90"
									>
										Save
									</button>
								</div>
							</div>
						</Form>
					)}
				</Formik>
			</div>
		</div>
	);
};

export default EditPlanModal;
